import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  linkedSignal,
} from '@angular/core';
import { LANDIN_CONFIG } from 'src/app/app.config';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { SendMessageModalComponent } from '../../../shared/Components/menutech-navbar/send-message-modal/send-message-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { State } from 'src/app/reducers';
import { selectUser } from 'src/app/shared/user/ngrx/user.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectLoginState } from 'src/app/auth/ngrx/auth.selectors';
import { LanguageSelectorComponent } from '../../../shared/Components/language-selector/language-selector.component';
import { InterfaceLanguage } from '../../../shared/constants/languages';
import { setInterfaceLang } from '../../../shared/ngrx/shared.actions';
import { Language } from '../../../shared/Models/models';
import { ActivatedRoute } from '@angular/router';
@Component({
  selector: 'app-footer',
  template: `
    <div class="footer" [style.background-color]="backgroundColor()">
      <div class="footer-container" [class]="footerClass()">
        @if (langSwitcher()) {
          <language-selector
            class="lang"
            [model]="language()"
            [prefix]="true"
            [simple]="true"
            [left]="true"
            [longLang]="true"
            changeValue="current"
            (changed)="changeLanguage()"
          />
        } @else {
          <span>&nbsp;</span>
        }

        <ul>
          @for (item of items; track item) {
            <li>
              <a
                class="mat-caption"
                [href]="url + (directory | transloco) + (item.link | transloco)"
                rel="noopener"
                target="_blank"
              >
                @if (item.icon) {
                  <mat-icon class="icon">{{ item.icon }}</mat-icon>
                }
                {{ item.label | transloco }}
              </a>
            </li>
          }
          @if (isLoggedIn()) {
            <li>
              <button class="mat-caption" (click)="showFeedbackDialog()">
                <mat-icon class="icon">favorite_border</mat-icon>
                {{ 'shared.footer.labels.feedback' | transloco }}
              </button>
            </li>
          }
        </ul>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        width: 100%;
        align-self: flex-end;
      }
      .footer {
        width: 100%;
        padding: 0px 24px;
        box-sizing: border-box;
      }
      .footer-container {
        width: 100%;
        padding: 16px 0px;
        border-top: 1px solid #dee4ed;
        box-sizing: border-box;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
      }
      .footer-container.blue-border {
        border-top: 1px solid var(--app-primary-500);
      }
      ul {
        display: flex;
        align-items: center;
        gap: 32px;
        list-style: none;
        margin: 0;
        padding: 0;
      }
      li {
        display: inline-block;
      }
      a,
      button {
        display: flex;
        align-items: center;
        gap: 4px;
        text-decoration: underline;
      }
      a > .mat-icon,
      button > .mat-icon {
        width: 14px;
        height: 14px;
        font-size: 14px;
      }
      button {
        background-color: transparent;
        border: none;
        cursor: pointer;
      }
      @media only screen and (max-width: 767px) {
        .footer {
          display: block;
          text-align: center;
          padding: 0px 16px;
        }
      }
      @media only screen and (max-width: 450px) {
        .footer {
          background-color: var(--app-primary-50);
        }
        .footer-container {
          justify-content: center;
          border-top: 0 !important;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, TranslocoPipe, LanguageSelectorComponent],
})
export class FooterComponent {
  private ngrxStore = inject<Store<State>>(Store);
  private dialog = inject(MatDialog);
  private translate = inject(TranslocoService);
  private router = inject(ActivatedRoute);

  readonly lang = input.required<InterfaceLanguage>();
  readonly langSwitcher = input<boolean>(true);
  readonly backgroundColor = input<string>();
  readonly footerClass = input<string | null>(null);

  language = linkedSignal(() => ({
    current: this.translate.activeLang() as InterfaceLanguage,
  }));

  currentYear;
  url = LANDIN_CONFIG.value;
  directory = 'shared.footer.directory';

  items = [
    {
      icon: null,
      link: 'shared.footer.links.imprint',
      label: 'shared.footer.labels.imprint',
    },
    {
      icon: null,
      link: 'shared.footer.links.privacy',
      label: 'shared.footer.labels.privacy',
    },
    {
      icon: null,
      link: 'shared.footer.links.terms_link',
      label: 'shared.footer.labels.terms_link',
    },
  ];

  user = toSignal(this.ngrxStore.select(selectUser), { initialValue: null });
  isLoggedIn = toSignal(this.ngrxStore.select(selectLoginState), {
    initialValue: false,
  });

  constructor() {
    const d = new Date();
    this.currentYear = d.getFullYear();
    this.router.queryParams.subscribe(this.handleLang);
  }
  showFeedbackDialog(): void {
    this.dialog.open(SendMessageModalComponent, {
      data: {
        subject: 3,
        user: this.user(),
      },
      width: '750px',
      height: this.user().email.includes('@partners.menutech.com')
        ? '650px'
        : '610px',
      panelClass: 'send-message-panel',
      autoFocus: false,
    });
  }

  changeLanguage() {
    const current = this.language().current;
    this.language.set({ current });
    this.translate.setActiveLang(current);
    this.ngrxStore.dispatch(setInterfaceLang({ lang: current }));
  }

  handleLang = ({ lang }: { lang: string }) => {
    if (this.isLoggedIn()) return;
    if (lang) {
      const l = Language(lang);
      this.language.set({ current: l });
      this.translate.setActiveLang(l);
      this.ngrxStore.dispatch(setInterfaceLang({ lang: l }));
    }
  };
}
